import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const serializeValue = (value: any) => btoa(JSON.stringify(value));

const deserializeValue = (serializedValue: string | null) => {
  if (serializedValue == null) return undefined;
  try {
    return JSON.parse(atob(serializedValue));
  } catch (e) {
    console.warn(e, serializedValue);
    return undefined;
  }
};

export function useSerializedQueryParam<T>(
  name: string,
  initialValue: T,
): [T, (newValue: T) => void];
export function useSerializedQueryParam<T>(
  name: string,
): [T | undefined, (newValue: T | undefined) => void];
export function useSerializedQueryParam<T>(
  name: string,
  initialValue?: T,
): [T | undefined, (newValue: T | undefined) => void] {
  const initialParams: Record<string, string> = {};

  if (initialValue != null) {
    initialParams[name] = serializeValue(initialValue);
  }

  const [searchParams, setSearchParams] = useSearchParams(initialParams);

  const value = useMemo(
    () => deserializeValue(searchParams.get(name)) as T | undefined,
    [name, searchParams],
  );

  const setValue = useCallback(
    (value: T | undefined) => {
      if (value == null) {
        searchParams.delete(name);
      } else {
        searchParams.set(name, serializeValue(value));
      }
      setSearchParams(searchParams);
      return value;
    },
    [name, searchParams, setSearchParams],
  );

  return [value, setValue];
}
